import * as React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { UserApi } from "@/api/user.api";
import { toast } from "sonner";
import { MFASetupSheet } from "@/components/users/MFASetupSheet";
import { useUserInfo } from "@/hooks/use-user-info";

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(6, "Password must be at least 6 characters"),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

const Settings = () => {
  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const [isChanging, setIsChanging] = React.useState(false);
  const [mfaSheetOpen, setMfaSheetOpen] = React.useState(false);
  const [qrCodeData, setQrCodeData] = React.useState<string | null>(null);
  const [mfaEnabled, setMfaEnabled] = React.useState<boolean | null>(null);

  const { userInfo } = useUserInfo();

  React.useEffect(() => {
    if (userInfo?.username) {
      UserApi.getUser({ username: userInfo.username })
        .then((res: any) => {
          setMfaEnabled(res.data.mfaEnabled);
        })
        .catch(() => setMfaEnabled(null));
    }
  }, [userInfo?.username]);

  const handleChangePassword = async (data: any) => {
    setIsChanging(true);
    try {
      await UserApi.changePassword(data);
      toast.success("Password changed successfully");
      passwordForm.reset();
    } catch (err: any) {
      if (err?.response?.status === 400 && err?.response?.data?.errorMessage) {
        toast.error(err.response.data.errorMessage);
      } else {
        toast.error(err?.message || "Failed to change password");
      }
    } finally {
      setIsChanging(false);
    }
  };

  const handleMfaToggle = async (checked: boolean) => {
    setMfaSheetOpen(checked);
    if (checked) {
      try {
        const response = await UserApi.generateMfaQrCode({
          username: userInfo?.username,
        });
        setQrCodeData(response.data.qrCodeData);
      } catch (err: any) {
        toast.error(err?.message || "Failed to generate QR code");
        setQrCodeData(null);
      }
    } else {
      setQrCodeData(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your dashboard preferences and system settings
        </p>
      </div>

      <Tabs defaultValue="notifications" className="space-y-4">
        <TabsList>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Receive email notifications for important events
                    </p>
                  </div>
                  <Switch defaultChecked={true} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">New Program Alerts</p>
                    <p className="text-sm text-muted-foreground">
                      Get notified when a new loyalty program is created
                    </p>
                  </div>
                  <Switch defaultChecked={true} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Transaction Reports</p>
                    <p className="text-sm text-muted-foreground">
                      Receive daily transaction summary reports
                    </p>
                  </div>
                  <Switch defaultChecked={false} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Error Alerts</p>
                    <p className="text-sm text-muted-foreground">
                      Be notified about failed transactions or system errors
                    </p>
                  </div>
                  <Switch defaultChecked={true} />
                </div>
              </div>
              <Button>Save Preferences</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>
                Manage your security preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form
                onSubmit={passwordForm.handleSubmit(handleChangePassword)}
                className="space-y-4"
              >
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      {...passwordForm.register("currentPassword")}
                    />
                    <p className="text-xs text-red-500">
                      {passwordForm.formState.errors.currentPassword?.message}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      {...passwordForm.register("newPassword")}
                    />
                    <p className="text-xs text-red-500">
                      {passwordForm.formState.errors.newPassword?.message}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...passwordForm.register("confirmNewPassword")}
                    />
                    <p className="text-xs text-red-500">
                      {
                        passwordForm.formState.errors.confirmNewPassword
                          ?.message
                      }
                    </p>
                  </div>
                </div>
                <Button className="mt-4" type="submit" disabled={isChanging}>
                  {isChanging ? "Updating..." : "Update Security Settings"}
                </Button>
              </form>
              <div className="grid gap-4 mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-muted-foreground">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                  <Switch
                    checked={mfaSheetOpen || !!mfaEnabled}
                    onCheckedChange={handleMfaToggle}
                    disabled={mfaEnabled === true}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Session Timeout</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically log out after 30 minutes of inactivity
                    </p>
                  </div>
                  <Switch defaultChecked={true} />
                </div>
              </div>
            </CardContent>
          </Card>
          <MFASetupSheet
            open={mfaSheetOpen}
            onOpenChange={setMfaSheetOpen}
            userId={"currentUserId"}
            userName={"Current User"}
            qrCodeData={qrCodeData}
            username={userInfo?.username}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
